import React, { useState, useEffect, useCallback } from 'react';
import AssetHierarchyImportCard from './AssetHierarchyImportCard';
import { isEmpty, curry } from 'lodash';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useApolloClient } from '@apollo/client';
import { IMPORT_ASSET_HIERARCHY } from '../mutations';
import { ASSET_HIERARCHY_IMPORT_STATUS_CHANGED } from '../subscriptions';
import io from '../../list/io';

const AssetHierarchyImportContainer = () => {
  const client = useApolloClient();

  const [importDisabled, setImportDisabled] = useState(false);

  const [sourcesData, setSourcesData] = useState({
    assetHierarchyImport: {
      status: [],
      importRunning: false,
      selectedProviders: [],
      enabledProviders: []
    }
  });

  useQuery(io.queries.ASSET_HIERARCHY_SOURCES_QUERY, {
    onCompleted: ({ assetHierarchySources }) => {
      setSourcesData({
        ...sourcesData,
        assetHierarchyImport: {
          ...sourcesData.assetHierarchyImport,
          enabledProviders: assetHierarchySources
        }
      });
    }
  });

  const [importAssetHierarchy] = useMutation(IMPORT_ASSET_HIERARCHY);

  useSubscription(ASSET_HIERARCHY_IMPORT_STATUS_CHANGED, {
    onData: data => {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'assetHierarchies' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'assetHierarchiesType' });
      const actualStatus = [...sourcesData.assetHierarchyImport.status];
      actualStatus.push({ ...data.data.data.assetHierarchyImportStatusChanged });
      setSourcesData({
        ...sourcesData,
        assetHierarchyImport: { ...sourcesData.assetHierarchyImport, status: actualStatus }
      });
    }
  });

  const onChangeAssetHierarchyImportData = useCallback(
    (field, value) => {
      if (sourcesData.assetHierarchyImport[field] === value) return;
      setSourcesData({
        ...sourcesData,
        assetHierarchyImport: { ...sourcesData.assetHierarchyImport, [field]: value }
      });
    },
    [sourcesData]
  );

  const onSelectProvider = curry((prop, value) => {
    setSourcesData(prevState => {
      const selectedProviders = value
        ? [...prevState.assetHierarchyImport.selectedProviders, prop]
        : prevState.assetHierarchyImport.selectedProviders.filter(p => p !== prop);

      return {
        ...prevState,
        assetHierarchyImport: {
          ...prevState.assetHierarchyImport,
          selectedProviders
        }
      };
    });
  });

  const onStartImport = () => {
    importAssetHierarchy({
      variables: {
        input: {
          providers: sourcesData.assetHierarchyImport.selectedProviders
        }
      }
    });

    setSourcesData(prevState => ({
      ...prevState,
      assetHierarchyImport: { ...prevState.assetHierarchyImport, status: [], importRunning: true }
    }));
  };

  useEffect(() => {
    const checkIfFinalEventExists = providerCode => {
      const status = sourcesData.assetHierarchyImport.status.filter(
        s => s.providerCode === providerCode && (s.progress === 100 || s.progress === 0)
      );
      return status.length !== 0;
    };

    if (
      !sourcesData ||
      sourcesData.assetHierarchyImport.status.length === 0 ||
      isEmpty(sourcesData.assetHierarchyImport.selectedProviders)
    ) {
      return;
    }

    const allImportsCompleted = sourcesData.assetHierarchyImport.selectedProviders.every(provider => checkIfFinalEventExists(provider));

    if (allImportsCompleted) {
      onChangeAssetHierarchyImportData('importRunning', false);
    }
  }, [sourcesData, onChangeAssetHierarchyImportData]);

  useEffect(() => {
    const disabled = isEmpty(sourcesData.assetHierarchyImport.selectedProviders);
    setImportDisabled(disabled);
  }, [sourcesData.assetHierarchyImport.selectedProviders]);

  return (
    <AssetHierarchyImportCard
      onStartImport={onStartImport}
      onSelectProvider={onSelectProvider}
      importDisabled={importDisabled}
      importStatus={sourcesData.assetHierarchyImport ? sourcesData.assetHierarchyImport.status : []}
      running={sourcesData.assetHierarchyImport ? sourcesData.assetHierarchyImport.importRunning : false}
      enabledImportProviders={sourcesData.assetHierarchyImport ? sourcesData.assetHierarchyImport.enabledProviders : []}
      selectedImportProviders={sourcesData.assetHierarchyImport ? sourcesData.assetHierarchyImport.selectedProviders : []}
    />
  );
};

export default AssetHierarchyImportContainer;
